import { CustomerModel } from "../../../../factories/customers/models/customerModel";
import { EmailTo } from "../../../../factories/emails/models/emails.models";
import { EMAIL_TEMPLATE_PLACEHOLDERS } from "./emailTemplates.constants";
import { EmailTemplateContext } from "./emailTemplates.types";

export type EmailTemplateRecipient = EmailTo & {
  customerName?: string;
  businessContactId?: string | null;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  contactPersonFirstName?: string;
  contactPersonLastName?: string;
};

const splitName = (name?: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);

  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const valueOrUndefined = (value?: string | null) =>
  value?.trim() ? value : undefined;

export const resolveEmailTemplate = (
  value: string,
  context: EmailTemplateContext,
) =>
  EMAIL_TEMPLATE_PLACEHOLDERS.reduce(
    (result, placeholder) =>
      result.replaceAll(
        placeholder.token,
        context[placeholder.key] ?? placeholder.token,
      ),
    value,
  );

export const buildEmailTemplateContext = (
  recipient?: EmailTemplateRecipient,
  invoiceCustomerName?: string | null,
): EmailTemplateContext => {
  const displayName =
    recipient?.customerName || recipient?.name || invoiceCustomerName || "";
  const splitDisplayName = splitName(displayName);
  const isOrganization = Boolean(recipient?.businessContactId);

  return {
    firstName: isOrganization
      ? valueOrUndefined(recipient?.contactPersonFirstName)
      : valueOrUndefined(recipient?.firstName) ||
        valueOrUndefined(splitDisplayName.firstName),
    lastName: isOrganization
      ? valueOrUndefined(recipient?.contactPersonLastName)
      : valueOrUndefined(recipient?.lastName) ||
        valueOrUndefined(splitDisplayName.lastName),
    companyName: isOrganization
      ? valueOrUndefined(recipient?.companyName) ||
        valueOrUndefined(displayName)
      : undefined,
  };
};

export const mapCustomerToTemplateRecipient = (
  customer: CustomerModel,
): EmailTemplateRecipient => {
  const splitCustomerName = splitName(customer.customerName);
  const contactPerson = customer.businessContact?.contactPersons;
  const isOrganization = Boolean(customer.businessContactId);

  return {
    email: customer.emailAddress,
    name: customer.customerName,
    customerId: customer.customerId,
    customerName: customer.customerName,
    businessContactId: customer.businessContactId,
    firstName: customer.contact?.firstName || splitCustomerName.firstName,
    lastName: customer.contact?.lastName || splitCustomerName.lastName,
    companyName: customer.businessContact?.companyName || customer.customerName,
    contactPersonFirstName: isOrganization
      ? contactPerson?.firstName || ""
      : undefined,
    contactPersonLastName: isOrganization
      ? contactPerson?.lastName || ""
      : undefined,
  };
};
